import * as geohash from "ngeohash";

/**
 * Precision (number of geohash characters) at which spatial geohashes are
 * stored across the schema (hiiveLocation, event, venue). Indexed lookups use
 * prefix ranges, so callers must never ask for a finer precision than stored.
 */
export const STORED_GEOHASH_PRECISION = 4;

/**
 * Precision levels for different search radii
 * Based on geohash precision vs approximate area coverage:
 * - Precision 3: ~156km x 156km
 * - Precision 4: ~39km x 19.5km
 * - Precision 5: ~4.9km x 4.9km
 * - Precision 6: ~1.2km x 0.6km
 * - Precision 7: ~153m x 153m
 */
const PRECISION_MAP = [
  { radius: 50, precision: 3 }, // 50km+ radius -> ~156km cells
  { radius: 20, precision: 4 }, // 20-50km radius -> ~39km cells
  { radius: 5, precision: 5 }, // 5-20km radius -> ~4.9km cells
  { radius: 2, precision: 6 }, // 2-5km radius -> ~1.2km cells
  { radius: 0.5, precision: 7 }, // 0.5-2km radius -> ~153m cells
] as const;

/**
 * Pick appropriate geohash precision based on search radius
 */
export function pickPrecision(radiusKm: number): number {
  for (const { radius, precision } of PRECISION_MAP) {
    if (radiusKm >= radius) {
      return precision;
    }
  }
  return 7; // Default to highest precision for very small radii
}

/**
 * Generate a geohash for given coordinates
 */
export function generateGeoHash(
  latitude: number,
  longitude: number,
  precision = 9,
): string {
  return geohash.encode(latitude, longitude, precision);
}

/**
 * Get geohash neighbors for proximity search
 */
export function getGeoHashNeighbors(geoHash: string): string[] {
  return geohash.neighbors(geoHash);
}

/**
 * Calculate distance between two coordinates in kilometers
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Decode a geohash back to coordinates (for debugging/testing)
 */
export function decodeGeoHash(geoHash: string): {
  latitude: number;
  longitude: number;
} {
  const decoded = geohash.decode(geoHash);
  return {
    latitude: decoded.latitude,
    longitude: decoded.longitude,
  };
}

/**
 * Get the bounding box for a geohash (for debugging/testing)
 */
export function getGeoHashBounds(geoHash: string) {
  return geohash.decode_bbox(geoHash);
}
